import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import {
  LoadContentDocument,
  TrainingsEntitiesContentBlockStatusEnum,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

import { contentRegistry } from './ContentRegistry'

function getCompletionStatus({ status }: { status: TrainingsEntitiesContentBlockStatusEnum }) {
  switch (status) {
    case 'completed': {
      return {
        status: 'positive',
        label: i18n.t('trainings.content.syllabus.status.completed'),
      }
    }
    case 'inprogress': {
      return {
        status: 'info',
        label: i18n.t('trainings.content.syllabus.status.in_progress'),
      }
    }
    case 'notstarted': {
      return {
        status: 'neutral',
        label: i18n.t('trainings.content.syllabus.status.not_started'),
      }
    }
  }
}

export default function Syllabus({
  trainingId,
  employeeId,
}: {
  trainingId: number
  employeeId?: number
}) {
  const source = useGraphqlDataCollectionSource({
    document: LoadContentDocument,
    mapData: (data) => {
      return graphqlContentMapper
        .fromGraphQL(data.trainings.contentModules, Number(trainingId))
        .modules.flatMap((module) => {
          return module.blocks.map((block) => {
            return {
              moduleReference: `${module.uuid}:${module.title}:${module.blocks.length}`, // THIS IS CRAZY, I KNOW. But fetching the collection again to count groups is crazier.
              block,
            }
          })
        })
    },
    mapPagination: (data) => {
      return { total: data.trainings.contentModules.length, hasNextPage: false } // We're loading the whole thing at once.
    },
    mapVariables() {
      return {
        trainingId,
        employeeId: employeeId,
      }
    },
    itemUrl: () => {
      return navigation.myTrainings.revamp.detailsRevamp.content.fullPath({
        trainingId: trainingId,
      })
    },
    paginationType: 'infinite-scroll', // This is a HACK to hide the bottom pagination. Since we load the whole thing at once, it should be fine.
    grouping: {
      hideSelector: true,
      collapsible: true,
      mandatory: true,
      defaultOpenGroups: true,
      groupBy: {
        moduleReference: {
          name: 'Module', // This is never shown, as it's the selector is hidden
          label(groupId) {
            return parseGroupId(groupId).title
          },
          itemCount(groupId) {
            return parseGroupId(groupId).count
          },
        },
      },
    },
  })

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  return (
    <Box paddingLeft="s20" paddingRight="s20">
      <OneDataCollection
        emptyStates={{
          'no-data': {
            emoji: '🧩',
            title: i18n.t('trainings.content.empty.header'),
            description: i18n.t('trainings.content.empty.description'),
            actions: [
              {
                icon: Add,
                label: i18n.t('trainings.content.empty.button'),
                onClick: () => {
                  navigate(
                    navigation.trainings.list.details_revamp.content.builder.fullPath({
                      id: trainingId,
                    })
                  )
                },
                variant: 'outline',
              },
            ],
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition({ block }) {
                const description = contentRegistry.fetch(block, 'label')?.()
                const icon = contentRegistry.fetch(block, 'icon')

                return {
                  title: String(block.title),
                  description: description ? [description] : undefined,
                  avatar: {
                    type: 'icon',
                    icon,
                    'aria-label': description,
                  },
                }
              },
              fields: employeeId
                ? [
                    {
                      label: 'Status',
                      render({ block }) {
                        const completionStatus = getCompletionStatus({
                          status: block.status,
                        })
                        return {
                          type: 'status',
                          value: completionStatus,
                        }
                      },
                    },
                  ]
                : [],
            },
          },
        ]}
      />
    </Box>
  )
}

function parseGroupId(groupId: unknown): {
  uuid: string
  title: string
  count: number
} {
  switch (typeof groupId) {
    case 'string': {
      const uuid = groupId.split(':')[0]
      const title = groupId.split(':')[1]
      const count = groupId.split(':')[2]

      return {
        uuid: String(uuid),
        title: String(title),
        count: Number(count),
      }
    }
    default: {
      throw 'Unknown groupId type'
    }
  }
}
